import { Search as SearchIcon, Menu as MenuIcon, ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, Close as CloseIcon, Home as HomeIcon } from '@/components/ui/qicons';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Drawer,
  Dialog,
  DialogContent,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  Chip,
  alpha,
} from '@mui/material';
import { UserMenu } from './UserMenu';
import { navigationConfig, NavigationSection, NavigationItem } from './navigationConfig';

// Interface for flattened search results
interface SearchResult {
  label: string;
  path: string;
  section: string;
  sectionIcon: React.ReactElement;
  breadcrumb: string;
}

interface OpenTab {
  id: string;
  label: string;
  path: string;
  icon?: React.ReactElement;
  closeable: boolean;
}

export const HorizontalNavBar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  // Track open tabs - Home is always open and not closeable
  const [openTabs, setOpenTabs] = useState<OpenTab[]>([
    { id: 'home', label: 'Home', path: '/dashboard', icon: <HomeIcon fontSize="small" />, closeable: false },
  ]);

  // Menu state for "more" sections
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Search state
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Flatten all navigation items for search
  const allSearchableItems = useMemo((): SearchResult[] => {
    const results: SearchResult[] = [];

    const flattenItems = (
      items: NavigationItem[],
      section: NavigationSection,
      parentLabel?: string
    ) => {
      for (const item of items) {
        const breadcrumb = parentLabel
          ? `${section.label} > ${parentLabel} > ${item.label}`
          : `${section.label} > ${item.label}`;

        results.push({
          label: item.label,
          path: item.path,
          section: section.label,
          sectionIcon: section.icon,
          breadcrumb,
        });

        if (item.children) {
          flattenItems(item.children, section, item.label);
        }
      }
    };

    for (const section of navigationConfig) {
      flattenItems(section.children, section);
    }

    return results;
  }, []);

  // Filter search results based on query
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    return allSearchableItems
      .filter(
        (item) =>
          item.label.toLowerCase().includes(query) ||
          item.breadcrumb.toLowerCase().includes(query)
      )
      .slice(0, 15); // Limit to 15 results
  }, [searchQuery, allSearchableItems]);

  // Handle search item click
  const handleSearchItemClick = (path: string) => {
    navigate(path);
    setSearchOpen(false);
    setSearchQuery('');
  };

  // Handle search dialog open
  const handleSearchOpen = () => {
    setSearchOpen(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  // Handle search dialog close
  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchQuery('');
  };

  // Keyboard shortcut for search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        handleSearchOpen();
      }
      if (e.key === 'Escape' && searchOpen) {
        handleSearchClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchOpen]);

  // Find current active tab
  const activeTabIndex = openTabs.findIndex(tab =>
    location.pathname === tab.path || location.pathname.startsWith(tab.path + '/')
  );

  // Determine which section we're in based on path
  const getCurrentSection = (): NavigationSection | undefined => {
    for (const section of navigationConfig) {
      if (section.children.some(child =>
        location.pathname === child.path ||
        location.pathname.startsWith(child.path + '/') ||
        child.children?.some(subChild =>
          location.pathname === subChild.path ||
          location.pathname.startsWith(subChild.path + '/')
        )
      )) {
        return section;
      }
    }
    return undefined;
  };

  // Auto-add tab when navigating to a new section
  useEffect(() => {
    const currentSection = getCurrentSection();
    if (currentSection) {
      // Check if tab already exists using functional update to get latest state
      setOpenTabs(prev => {
        // If tab already exists, don't add it again
        if (prev.find(tab => tab.id === currentSection.id)) {
          return prev;
        }

        // Find the first matching path in the section
        let matchingPath = '';
        for (const child of currentSection.children) {
          if (location.pathname === child.path || location.pathname.startsWith(child.path + '/')) {
            matchingPath = child.path;
            break;
          }
          if (child.children) {
            for (const subChild of child.children) {
              if (location.pathname === subChild.path || location.pathname.startsWith(subChild.path + '/')) {
                matchingPath = subChild.path;
                break;
              }
            }
          }
        }

        if (matchingPath) {
          return [...prev, {
            id: currentSection.id,
            label: currentSection.label,
            path: matchingPath || currentSection.children[0]?.path || '/dashboard',
            icon: currentSection.icon,
            closeable: true,
          }];
        }

        return prev;
      });
    }
  }, [location.pathname]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const tab = openTabs[newValue];
    if (tab) {
      navigate(tab.path);
    }
  };

  const handleCloseTab = (event: React.MouseEvent, tabId: string) => {
    event.stopPropagation();
    const tabToClose = openTabs.find(t => t.id === tabId);
    if (tabToClose?.closeable) {
      const newTabs = openTabs.filter(t => t.id !== tabId);
      setOpenTabs(newTabs);

      // Navigate to home if closing current tab
      if (location.pathname.includes(tabToClose.path.split('/')[1])) {
        navigate('/dashboard');
      }
    }
  };

  const handleOpenSection = (section: NavigationSection) => {
    const existingTab = openTabs.find(t => t.id === section.id);

    if (existingTab) {
      navigate(existingTab.path);
    } else {
      const newTab: OpenTab = {
        id: section.id,
        label: section.label,
        path: section.children[0]?.path || '/dashboard',
        icon: section.icon,
        closeable: true,
      };
      setOpenTabs(prev => [...prev, newTab]);
      navigate(newTab.path);
    }
    setMoreMenuAnchor(null);
  };

  const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreClose = () => {
    setMoreMenuAnchor(null);
  };

  // Sections not currently open as tabs
  const availableSections = navigationConfig.filter(
    section => !openTabs.find(tab => tab.id === section.id) && section.id !== 'home'
  );

  // Mobile drawer content
  const mobileDrawerContent = (
    <Box sx={{ width: 280, pt: 2 }}>
      <Box sx={{ px: 2, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Navigation</Typography>
        <IconButton onClick={() => setMobileDrawerOpen(false)}>
          <ChevronLeftIcon />
        </IconButton>
      </Box>
      {navigationConfig.map(section => (
        <MenuItem
          key={section.id}
          onClick={() => {
            handleOpenSection(section);
            setMobileDrawerOpen(false);
          }}
          sx={{
            py: 1.5,
            backgroundColor: openTabs.find(t => t.id === section.id)
              ? theme.palette.action.selected
              : 'transparent',
          }}
        >
          <ListItemIcon>{section.icon}</ListItemIcon>
          <ListItemText>{section.label}</ListItemText>
        </MenuItem>
      ))}
    </Box>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: 48,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: 1,
      }}
    >
      {/* Left Section: Menu/Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {isMobile && (
          <IconButton
            onClick={() => setMobileDrawerOpen(true)}
            sx={{ color: theme.palette.text.primary }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo */}
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: theme.palette.primary.main,
            cursor: 'pointer',
            display: { xs: 'none', sm: 'block' },
          }}
          onClick={() => navigate('/dashboard')}
        >
          QIYAS Analytics
        </Typography>
      </Box>

      {/* Center Section: Tabs */}
      {!isMobile && (
        <Box
          ref={tabsContainerRef}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            mx: 2,
            overflow: 'hidden',
          }}
        >
          <Tabs
            value={activeTabIndex >= 0 ? activeTabIndex : false}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 48,
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.secondary.main,
                height: 2,
              },
              '& .MuiTabs-scroller': {
                overflow: 'hidden !important',
              },
            }}
          >
            {openTabs.map((tab, index) => (
              <Tab
                key={tab.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {tab.icon && React.cloneElement(tab.icon, {
                      sx: { fontSize: 18, mr: 0.5 }
                    })}
                    <Typography variant="body2" noWrap sx={{ maxWidth: 120 }}>
                      {tab.label}
                    </Typography>
                    {tab.closeable && (
                      <Box
                        component="span"
                        onClick={(e) => handleCloseTab(e, tab.id)}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: 0.25,
                          ml: 0.5,
                          borderRadius: '4px',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </Box>
                    )}
                  </Box>
                }
                sx={{
                  minHeight: 48,
                  textTransform: 'none',
                  color: theme.palette.text.secondary,
                  '&.Mui-selected': {
                    color: theme.palette.text.primary,
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'rgba(0,0,0,0.04)',
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(0,0,0,0.08)',
                  },
                }}
              />
            ))}
          </Tabs>

          {/* Add Tab Button */}
          {availableSections.length > 0 && (
            <>
              <Tooltip title="Open section">
                <IconButton
                  onClick={handleMoreClick}
                  sx={{
                    ml: 1,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={moreMenuAnchor}
                open={Boolean(moreMenuAnchor)}
                onClose={handleMoreClose}
                PaperProps={{
                  sx: {
                    maxHeight: 400,
                    width: 250,
                  },
                }}
              >
                {availableSections.map(section => (
                  <MenuItem
                    key={section.id}
                    onClick={() => handleOpenSection(section)}
                  >
                    <ListItemIcon>{section.icon}</ListItemIcon>
                    <ListItemText>{section.label}</ListItemText>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      )}

      {/* Right Section: Search & User Menu */}
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 1 }}>
        {!isMobile && (
          <Tooltip title="Search (Ctrl+K)">
            <IconButton
              onClick={handleSearchOpen}
              sx={{ color: theme.palette.text.secondary }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
        )}
        <UserMenu />
      </Box>

      {/* Search Dialog */}
      <Dialog
        open={searchOpen}
        onClose={handleSearchClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            position: 'absolute',
            top: '15%',
            m: 0,
            borderRadius: 2,
            bgcolor: 'background.paper',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Search Input */}
          <TextField
            inputRef={searchInputRef}
            fullWidth
            placeholder="Search pages... (e.g., Dashboard, Trading, Reports)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                '& fieldset': { border: 'none' },
              },
              '& .MuiInputBase-input': {
                py: 2,
                fontSize: '1rem',
              },
            }}
          />

          {/* Search Results */}
          {searchQuery && (
            <Box
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                maxHeight: 400,
                overflow: 'auto',
              }}
            >
              {searchResults.length > 0 ? (
                <List sx={{ py: 1 }}>
                  {searchResults.map((result, index) => (
                    <ListItemButton
                      key={`${result.path}-${index}`}
                      onClick={() => handleSearchItemClick(result.path)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        {React.cloneElement(result.sectionIcon, {
                          sx: { fontSize: 20, color: theme.palette.primary.main },
                        })}
                      </ListItemIcon>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {result.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: theme.palette.text.secondary }}
                        >
                          {result.breadcrumb}
                        </Typography>
                      </Box>
                      <Chip
                        label={result.section}
                        size="small"
                        sx={{
                          fontSize: '0.65rem',
                          height: 20,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: theme.palette.primary.main,
                        }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No pages found for "{searchQuery}"
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Try searching for "dashboard", "trading", "reports", etc.
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* Keyboard hint */}
          {!searchQuery && (
            <Box
              sx={{
                borderTop: `1px solid ${theme.palette.divider}`,
                py: 2,
                px: 2,
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                <kbd style={{
                  padding: '2px 6px',
                  borderRadius: 4,
                  backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#eee',
                  border: `1px solid ${theme.palette.divider}`,
                  fontFamily: 'monospace',
                }}>
                  ↵
                </kbd>{' '}
                to select
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <kbd style={{
                  padding: '2px 6px',
                  borderRadius: 4,
                  backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#eee',
                  border: `1px solid ${theme.palette.divider}`,
                  fontFamily: 'monospace',
                }}>
                  Esc
                </kbd>{' '}
                to close
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
      >
        {mobileDrawerContent}
      </Drawer>
    </Box>
  );
};
